import {CustomScrollbar} from '@/components/custom-scrollbar/custom-scrollbar';
import {EBreakpoint} from '@/common/scripts/constants';
import {ERestMethod} from '@/common/scripts/types/utils';
import {GenplanBuildings} from './buildings/GenplanBuildings';
import {GenplanRooms} from '@/sections/genplan/rooms/GenplanRooms';
// eslint-disable-next-line sort-imports
import type {ICallback, ICallbackRequest} from '@/common/scripts/types/utils';
import {InternetConnection, Utils} from '@/common/scripts/utils';
import {ZoomController} from '@/sections/genplan/zoom/ZoomController';

// Здание на генплане
export interface GenplanBuilding {
    id: string;
    markerPos: { x: number, y: number };
    name: string;
    endOfDevelopment: string;
    totalArea: string;
    forSaleRooms: { amount: string, startPrice: string };
    forRentRooms: { amount: string, startPrice: string };
    buttonText: string;
    schemaSrc: string;
    rooms: GenplanRoom[];
}

// Помещение в здании
export interface GenplanRoom {
    id: string;
    name: string;
    area: string;
    salePrice: string;
    rentPrice: string;
    svgPath: string;
    linkToRoomPage: string;
    status?: 'sailed' | 'reserved';
    inFavorite?: boolean;
}

export class Genplan {
    private readonly wrapper: HTMLElement;
    private buildingsManager: GenplanBuildings;
    private readonly roomsManager: GenplanRooms;
    private serverConnection: InternetConnection;
    private buildingsData: GenplanBuilding[];
    private mapScrollbar: CustomScrollbar;
    private roomsScrollbar: CustomScrollbar;

    constructor(wrapper: HTMLElement, markers?: GenplanBuilding[]) {
        this.wrapper = wrapper;

        this.roomsManager = new GenplanRooms(wrapper);
        this.serverConnection = new InternetConnection(this.loadData.bind(this));

        this.createScrollAndZoom();

        if (markers) {
            this.initializeData(markers);
        } else {
            this.serverConnection.connect();
        }
    }

    /**
     * Настраивает обработчики событий между компонентами
     */
    private bindEvents(): void {
        // Привязываем переход от зданий к помещениям
        this.buildingsManager.setOnGoToRoomsCallback((building: GenplanBuilding) => {
            this.roomsManager.show(building);
            this.roomsManager.scrollToFirstRoom();
        });
    }

    /**
     * Инициализирует компонент с переданными данными
     * @param {GenplanBuilding[]} markers - массив данных о зданиях на генплане
     */
    private initializeData(markers: GenplanBuilding[]): void {
        this.buildingsData = markers;
        this.buildingsManager = new GenplanBuildings(this.wrapper, markers);
        this.bindEvents();
        this.scrollToFirstMarker();
        this.initOverlay();
    }

    /**
     * Загружает данные генплана с сервера
     */
    private loadData(): void {
        const that = this;
        const dataUrl = this.wrapper.dataset['ajax']!;

        const callback: ICallback = {
            success(req: ICallbackRequest) {
                if (req.data) {
                    that.buildingsData = req.data as GenplanBuilding[];
                    that.initializeData(that.buildingsData);
                }
            },
            error(err: number) {
                console.error(`Genplan: Server error with status ${err}`);
            }
        };

        Utils.send(null, dataUrl, callback, ERestMethod.GET);
    }

    /**
     * Создаёт кастомный скролл и зум для обёртки карты и схемы помещений
     */
    private createScrollAndZoom(): void {
        // Настройки зума и скролла карты зданий
        const buildingScrollContainer = this.wrapper.querySelector<HTMLElement>('.j-genplan-map')!;
        const buildingScrollbar = new CustomScrollbar(buildingScrollContainer);


        const buildingZoom = new ZoomController(buildingScrollContainer, {
            min         : 1,
            max         : 2,
            step        : 0.2,
            initialScale: 1
        });

        buildingZoom.setScrollbar(buildingScrollbar);
        this.mapScrollbar = buildingScrollbar;


        // Настройки зума и скролла схемы помещений
        const roomsScrollContainer = this.wrapper.querySelector<HTMLElement>('.j-rooms-scrollbar')!;

        const roomsZoom = new ZoomController(roomsScrollContainer, {
            min         : Utils.isMobile() ? 0.6 : 1,
            max         : 1.4,
            step        : 0.2,
            initialScale: 1
        });

        const roomsScrollbar = new CustomScrollbar(roomsScrollContainer);

        roomsZoom.setScrollbar(roomsScrollbar);
        this.roomsScrollbar = roomsScrollbar;
        this.roomsManager.setScrollbar(roomsScrollbar);
        this.roomsManager.setZoomController(roomsZoom);
    }

    /**
     * Скроллит карту к первому маркеру здания
     */
    private scrollToFirstMarker(): void {
        const firstMarker = this.wrapper.querySelector<HTMLElement>('.j-genplan-marker')!;

        this.mapScrollbar.scrollToElement(firstMarker);
    }

    /**
     * Инициализирует touch-overlay
     */
    private initOverlay(): void {
        if (!Utils.isTouchDevice() || window.innerWidth >= EBreakpoint.DESKTOP) {
            return;
        }

        const overlay = this.wrapper.querySelector('.j-touch-overlay')!;

        const removeOverlay = (): void => {
            overlay.classList.add('is-hidden');
            overlay.removeEventListener('touchstart', removeOverlay);
        };

        overlay.addEventListener('touchstart', removeOverlay);
    }
}
